export const integrationPatternsModule = {
  id: "sd-integration-patterns",
  title: "Enterprise Integration Patterns",
  description: "Master integration strategies for connecting enterprise systems, APIs, and external services reliably.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Implement proven integration patterns for enterprise system connectivity
- Design robust API gateways and service meshes for complex architectures
- Use message queuing and event-driven patterns for reliable system integration
- Build fault-tolerant integrations that handle network failures and service outages

## Prerequisites
- Experience with REST APIs and web services
- Understanding of messaging systems and queues
- Knowledge of enterprise application architecture

## Overview
Enterprise integration is one of the most challenging aspects of modern system architecture. Poor integration design causes 40% of enterprise IT failures and costs organizations an average of $12M annually in downtime and data inconsistency issues.

This module provides battle-tested integration patterns from enterprise architects at companies like IBM, Microsoft, and SAP for building reliable, scalable integration solutions.

---

## Key Concepts

### Enterprise Integration Patterns Catalog
Different integration scenarios require different patterns based on coupling, reliability, and performance requirements.

#### Message Channel Patterns
**Point-to-Point Channel**: Direct communication between two systems
- Use for: Simple request-response, low-latency requirements
- Example: Order service calling payment service directly

**Publish-Subscribe Channel**: One-to-many event distribution
- Use for: Event notifications, real-time updates
- Example: Order events broadcast to inventory, shipping, and analytics services

**Dead Letter Channel**: Handle failed message processing
- Use for: Error handling, message replay, system reliability
- Example: Failed payment messages routed to manual review queue

#### Message Routing Patterns
**Content-Based Router**: Route messages based on content

function routeOrder(order) {
  if (order.type === 'PRIORITY') {
    return 'priority-fulfillment-queue';
  } else if (order.amount > 1000) {
    return 'high-value-order-queue';
  } else {
    return 'standard-order-queue';
  }
}

**Message Filter**: Process only relevant messages

// Only process orders for specific regions
const regionFilter = (message) => {
  const allowedRegions = ['US', 'CA', 'EU'];
  return allowedRegions.includes(message.order.region);
};

### API Gateway Architecture
API gateways provide a unified entry point for microservices while handling cross-cutting concerns.

#### Gateway Responsibilities
**Traffic Management**: Rate limiting, load balancing, circuit breaking
**Security**: Authentication, authorization, API key validation
**Monitoring**: Request logging, metrics collection, distributed tracing
**Transformation**: Request/response formatting, protocol translation

#### Gateway Pattern Implementation

// API Gateway Route Configuration
const gatewayConfig = {
  routes: [
    {
      path: '/api/orders/*',
      service: 'order-service',
      rateLimit: { requests: 1000, window: '1m' },
      auth: { required: true, scope: 'orders:read' }
    },
    {
      path: '/api/payments/*',
      service: 'payment-service',
      rateLimit: { requests: 500, window: '1m' },
      auth: { required: true, scope: 'payments:write' }
    }
  ],
  middleware: [
    'authentication',
    'rate-limiting',
    'request-logging',
    'response-caching'
  ]
};

### Event-Driven Integration Architecture
Event-driven patterns enable loose coupling and real-time responsiveness in enterprise systems.

#### Event Sourcing Pattern
**Concept**: Store all changes as events rather than current state
**Benefits**: Complete audit trail, temporal queries, event replay
**Challenges**: Event schema evolution, snapshot creation, query complexity

#### CQRS (Command Query Responsibility Segregation)
**Concept**: Separate read and write operations with different data models
**Benefits**: Optimized read/write performance, independent scaling
**Use Cases**: High-read applications, complex reporting requirements

---

## Practical Examples

### Example 1: E-commerce Integration Hub
Design a comprehensive integration solution for enterprise e-commerce platform.

**Integration Requirements**:
- Connect with 3 payment providers (PayPal, Stripe, bank transfers)
- Sync inventory with 5 suppliers via different protocols (REST, SOAP, FTP)
- Send order notifications to CRM, email service, and analytics platform
- Handle 10,000+ orders per day with <500ms response times

**Architecture Design**:

API Gateway connects Web Frontend to Order Service
Message Broker handles async communication
Integration Hub coordinates with external services
Payment Services include PayPal, Stripe, Bank APIs
Inventory Sync manages Supplier APIs and FTP
Notification Services handle CRM, Email, Analytics

**Message Flow Design**:

// Order Processing Integration Flow
class OrderIntegrationOrchestrator {
  async processOrder(order) {
    // 1. Validate order
    const validationResult = await this.validateOrder(order);
    if (!validationResult.valid) {
      throw new Error(validationResult.errors);
    }

    // 2. Process payment async
    await this.messageQueue.publish('payment-requests', {
      orderId: order.id,
      amount: order.total,
      paymentMethod: order.paymentMethod
    });

    // 3. Reserve inventory async
    await this.messageQueue.publish('inventory-reservations', {
      orderId: order.id,
      items: order.items
    });

    // 4. Send notifications async
    await this.messageQueue.publish('order-notifications', {
      type: 'ORDER_CREATED',
      orderId: order.id,
      customerId: order.customerId
    });

    return { orderId: order.id, status: 'PROCESSING' };
  }
}

### Example 2: Legacy System Integration
Integrate modern microservices with legacy enterprise systems.

**Challenge**: Connect React frontend → Spring Boot API → Legacy COBOL mainframe

**Integration Solution**:

// Legacy System Adapter Pattern
class MainframeBankingAdapter {
  constructor() {
    this.mqConnection = new IBMMQClient();
    this.requestTimeout = 30000;
  }

  async transferFunds(fromAccount, toAccount, amount) {
    // Transform modern API request to mainframe format
    const mainframeRequest = this.transformToMainframeFormat({
      transactionType: 'TRANSFER',
      fromAccount: fromAccount.padStart(12, '0'),
      toAccount: toAccount.padStart(12, '0'),
      amount: (amount * 100).toString().padStart(12, '0'), // Convert to cents
      timestamp: new Date().toISOString().replace(/[-:]/g, '')
    });

    try {
      // Send to mainframe via MQ
      const response = await this.mqConnection.sendRequest(
        'BANK.TRANSFER.REQUEST',
        mainframeRequest,
        { timeout: this.requestTimeout }
      );

      // Transform mainframe response to modern format
      return this.transformFromMainframeFormat(response);
    } catch (error) {
      // Handle mainframe-specific errors
      throw this.translateMainframeError(error);
    }
  }

  transformToMainframeFormat(request) {
    // Convert JSON to fixed-width format expected by mainframe
    return request.transactionType.padEnd(8) +
           request.fromAccount +
           request.toAccount +
           request.amount +
           request.timestamp;
  }
}

💡 **Pro Tip**: Implement the Strangler Fig pattern to gradually replace legacy systems by intercepting calls and routing to new services incrementally.

---

## Industry Adoption

### Integration Pattern Success Metrics
Organizations with mature integration architectures report:
- 55% reduction in system integration time
- 70% improvement in data consistency across systems
- 40% decrease in integration-related production issues
- 60% faster onboarding of new business partners

### Popular Integration Tools
- **Message Brokers**: Apache Kafka, RabbitMQ, AWS SQS for async messaging
- **Integration Platforms**: MuleSoft, Apache Camel, WSO2 for complex routing
- **API Management**: Kong, Apigee, AWS API Gateway for API lifecycle
- **Event Streaming**: Apache Kafka, AWS Kinesis for real-time data processing

---

## Key Takeaways

✅ **Essential Integration Principles**:
- Use message channels and routing patterns to decouple system dependencies
- Implement API gateways to centralize cross-cutting concerns and traffic management
- Apply event-driven patterns for real-time integration and system responsiveness
- Design integration adapters to bridge legacy systems with modern architectures

⚠️ **Common Integration Mistakes**:
- Creating point-to-point integrations that become unmaintainable spaghetti
- Ignoring message ordering and delivery guarantees in distributed systems
- Under-estimating network latency and failure scenarios in integration design

## Next Steps
Next, you'll learn performance optimization and reliability patterns for ensuring enterprise systems meet SLA requirements under high load.

---
`,
  duration: "4 hours",
  difficulty: "intermediate" as const,
  objectives: [
    "Implement enterprise integration patterns",
    "Design robust API gateways",
    "Use message queuing effectively",
    "Build fault-tolerant integrations"
  ],
  keyTopics: [
    "Integration Patterns",
    "API Gateway",
    "Message Routing",
    "Event-Driven Architecture",
    "Legacy System Integration"
  ]
};